import type { RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { isNotLive } from '@/api/types'
import { FreshnessBadge, Icon } from '@/design'
import { usePageFreshnessValue } from '@/lib/freshness'

import { GlobalFilterBar } from './GlobalFilterBar'
import { TenantSwitcher } from './TenantSwitcher'
import { UserMenu } from './UserMenu'

/**
 * The routed page's own provenance when it has declared one; otherwise the
 * client's — unless that says "not live", which is a page's to claim: a real
 * page still loading, or failing to, must not borrow it. Not-live pages
 * declare it themselves (`useNotLivePage`).
 */
function ShellFreshness() {
  const page = usePageFreshnessValue()
  const client = useApiClient()
  const freshness = useQuery({
    queryKey: ['freshness', client.kind],
    queryFn: () => client.freshness(),
    refetchInterval: 60_000,
  })
  return <FreshnessBadge meta={page ?? (isNotLive(freshness.data) ? null : freshness.data)} />
}

export function TopBar({
  drawerOpen,
  onMenu,
  menuRef,
}: {
  drawerOpen: boolean
  onMenu: () => void
  menuRef: RefObject<HTMLButtonElement | null>
}) {
  return (
    <header className="ci-topbar">
      <div className="ci-topbar__start">
        <button
          ref={menuRef}
          type="button"
          className="ci-button ci-topbar__menu"
          data-variant="ghost"
          aria-controls="ci-sidebar"
          aria-expanded={drawerOpen}
          onClick={onMenu}
        >
          <Icon name="menu" size={18} />
          <span className="ci-visually-hidden">Navigation</span>
        </button>
        <TenantSwitcher />
      </div>
      <GlobalFilterBar />
      <div className="ci-topbar__end">
        <ShellFreshness />
        <UserMenu />
      </div>
    </header>
  )
}
